import { createContext, useCallback, useContext, useState } from "react";

/** Optional scope when opening the agent from a dashboard (matches API `state` snake_case). */
export type ChatOpenScope = {
  dashboardId?: string;
  startDate?: string;
  endDate?: string;
};

interface ChatContextValue {
  openChatWithMessage: (message: string, scope?: ChatOpenScope) => void;
  /** Open the chat with the input pre-filled (NOT sent) — e.g. "+ Add widget". */
  openChatWithDraft: (draft: string, scope?: ChatOpenScope) => void;
  openChatForWidget: (widgetId: string, widgetTitle: string, dashboardId: string) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  initialMessage: string | null;
  clearInitialMessage: () => void;
  initialDraft: string | null;
  clearInitialDraft: () => void;
  activeWidgetId: string | null;
  activeWidgetTitle: string | null;
  activeDashboardId: string | null;
  dashboardStartDate: string | null;
  dashboardEndDate: string | null;
  clearWidgetContext: () => void;
  /** Currently loaded thread for the side ChatWidget. null = new conversation. */
  activeThreadId: string | null;
  setActiveThreadId: (threadId: string | null) => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

interface ChatProviderProps {
  children: React.ReactNode;
}

export function ChatProvider({ children }: ChatProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialMessage, setInitialMessage] = useState<string | null>(null);
  const [initialDraft, setInitialDraft] = useState<string | null>(null);
  const [activeWidgetId, setActiveWidgetId] = useState<string | null>(null);
  const [activeWidgetTitle, setActiveWidgetTitle] = useState<string | null>(null);
  const [activeDashboardId, setActiveDashboardId] = useState<string | null>(null);
  const [dashboardStartDate, setDashboardStartDate] = useState<string | null>(null);
  const [dashboardEndDate, setDashboardEndDate] = useState<string | null>(null);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  const openChatWithMessage = useCallback(
    (message: string, scope?: ChatOpenScope) => {
      setInitialMessage(message);
      if (scope?.dashboardId) {
        setActiveDashboardId(scope.dashboardId);
        setDashboardStartDate(scope.startDate ?? null);
        setDashboardEndDate(scope.endDate ?? null);
      } else {
        setActiveDashboardId(null);
        setDashboardStartDate(null);
        setDashboardEndDate(null);
      }
      setIsOpen(true);
    },
    []
  );

  const openChatForWidget = useCallback(
    (widgetId: string, widgetTitle: string, dashboardId: string) => {
      setActiveWidgetId(widgetId);
      setActiveWidgetTitle(widgetTitle);
      setActiveDashboardId(dashboardId);
      setDashboardStartDate(null);
      setDashboardEndDate(null);
      setIsOpen(true);
    },
    []
  );

  const openChatWithDraft = useCallback((draft: string, scope?: ChatOpenScope) => {
    setInitialDraft(draft);
    if (scope?.dashboardId) {
      setActiveDashboardId(scope.dashboardId);
      setDashboardStartDate(scope.startDate ?? null);
      setDashboardEndDate(scope.endDate ?? null);
    }
    setIsOpen(true);
  }, []);

  const clearInitialMessage = useCallback(() => {
    setInitialMessage(null);
  }, []);

  const clearInitialDraft = useCallback(() => {
    setInitialDraft(null);
  }, []);

  const clearWidgetContext = useCallback(() => {
    setActiveWidgetId(null);
    setActiveWidgetTitle(null);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        openChatWithMessage,
        openChatWithDraft,
        openChatForWidget,
        isOpen,
        setIsOpen,
        initialMessage,
        clearInitialMessage,
        initialDraft,
        clearInitialDraft,
        activeWidgetId,
        activeWidgetTitle,
        activeDashboardId,
        dashboardStartDate,
        dashboardEndDate,
        clearWidgetContext,
        activeThreadId,
        setActiveThreadId,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within ChatProvider");
  }
  return context;
}
